import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';
import DateRangeSelector from '../../../components/ui/DateRangeSelector';
import wellnessService from '../../../services/wellnessService';

const WellnessTrendChart = ({ data, className = "" }) => {
  const [dateRange, setDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate?.setDate(endDate?.getDate() - 14);
    return {
      startDate: startDate?.toISOString()?.split('T')?.[0],
      endDate: endDate?.toISOString()?.split('T')?.[0],
      preset: '14'
    };
  });
  const [showDateSelector, setShowDateSelector] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // State for toggling wellness metrics visibility
  const [visibleMetrics, setVisibleMetrics] = useState({
    sleep: true,
    fatigue: true,
    soreness: true,
    stress: true
  });

  // Load data based on date range
  useEffect(() => {
    loadDataForRange();
  }, [dateRange]);

  const loadDataForRange = async () => {
    try {
      setIsLoading(true);
      const data = await wellnessService?.getByDateRange(
        dateRange?.startDate,
        dateRange?.endDate
      );
      
      // No transformation needed - plot hooperIndex directly
      setFilteredData(data);
    } catch (error) {
      console.error('Error loading wellness data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange);
    setShowDateSelector(false);
  };
  
  const toggleMetric = (metric) => {
    setVisibleMetrics(prev => ({
      ...prev,
      [metric]: !prev?.[metric]
    }));
  };

  // Calculate date range duration in days
  const getDateRangeDays = () => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    return Math.ceil((end - start) / (24 * 60 * 60 * 1000)) + 1;
  };

  const dateRangeDays = getDateRangeDays();
  const needsScrolling = dateRangeDays > 60;

  // Calculate chart width for scrolling
  const getChartWidth = () => {
    if (needsScrolling) {
      // For ranges > 60 days, calculate width to show all data with scrolling
      // Use ~15px per day to ensure readability when scrolling
      return dateRangeDays * 15;
    }
    // For ranges ≤ 60 days, use 100% to fit container without scrolling
    return '100%';
  };

  // Enhanced tooltip showing ALL metrics for synchronized hover
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && label && filteredData?.length) {
      // Find the data point for this date to show all metrics
      const dataPoint = filteredData?.find(d => d?.date === label);
      
      if (dataPoint) {
        return (
          <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
            <p className="text-sm font-medium text-text-primary mb-2">
              {new Date(label)?.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short'
              })}
            </p>
            {/* Hooper Index */}
            <div className="flex items-center space-x-2 text-sm mb-1">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: '#2563EB' }}
              />
              <span className="text-text-secondary">Hooper Index:</span>
              <span className="font-medium text-text-primary">{dataPoint?.hooperIndex || '-'}</span>
            </div>
            {/* Sleep */}
            <div className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: '#059669' }}
              />
              <span className="text-text-secondary">Sleep:</span>
              <span className="font-medium text-text-primary">{dataPoint?.sleep || '-'}</span>
            </div>
            {/* Fatigue */}
            <div className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: '#D97706' }}
              />
              <span className="text-text-secondary">Fatigue:</span>
              <span className="font-medium text-text-primary">{dataPoint?.fatigue || '-'}</span>
            </div>
            {/* Soreness */}
            <div className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: '#DC2626' }}
              />
              <span className="text-text-secondary">Soreness:</span>
              <span className="font-medium text-text-primary">{dataPoint?.soreness || '-'}</span>
            </div>
            {/* Stress */}
            <div className="flex items-center space-x-2 text-sm">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: '#7C3AED' }}
              />
              <span className="text-text-secondary">Stress:</span>
              <span className="font-medium text-text-primary">{dataPoint?.stress || '-'}</span>
            </div>
          </div>
        );
      }
    }
    return null;
  };

  const formatDateRange = () => {
    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    
    const formatDate = (date) => {
      return date?.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    };
    
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  return (
    <div className={`bg-card rounded-lg p-6 border border-border ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-text-primary">Wellness Trend</h3>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-text-secondary">
            {formatDateRange()}
          </div>
          <div className="relative">
            <button
              onClick={() => setShowDateSelector(!showDateSelector)}
              className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Date Range
            </button>
            {showDateSelector && (
              <div className="absolute right-0 top-full mt-2 z-10">
                <DateRangeSelector
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  onClose={() => setShowDateSelector(false)}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="h-[480px] flex items-center justify-center">
          <div className="text-text-secondary">Loading data...</div>
        </div>
      ) : (
        /* Single shared horizontal scroll container */
        (<div 
          className="overflow-x-auto overflow-y-hidden"
          style={{ 
            width: '100%'
          }}
        >
          <div 
            className="flex flex-col space-y-6" 
            style={{ 
              width: getChartWidth(), 
              minWidth: '100%'
            }}
          >
            {/* Chart A (Top): Hooper Index Only */}
            <div className="bg-background/50 rounded-lg p-4 border border-border/50">
              <h4 className="text-sm font-medium text-text-secondary mb-2">Hooper Index</h4>
              <div style={{ height: '200px', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={filteredData} 
                    syncId="wellnessSync"
                    margin={{ top: 5, right: 60, left: 60, bottom: 5 }}
                  >
                    <defs>
                      {/* Gradient for blended colored background sections */}
                      <linearGradient id="hooperGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#DC2626" stopOpacity={0.2} /> {/* Red - Poor (28-21) */}
                        <stop offset="29%" stopColor="#DC2626" stopOpacity={0.2} />
                        <stop offset="29%" stopColor="#EA580C" stopOpacity={0.2} /> {/* Orange - Fair (21-17) */}
                        <stop offset="46%" stopColor="#EA580C" stopOpacity={0.2} />
                        <stop offset="46%" stopColor="#2563EB" stopOpacity={0.2} /> {/* Blue - Good (17-13) */}
                        <stop offset="63%" stopColor="#2563EB" stopOpacity={0.2} />
                        <stop offset="63%" stopColor="#166534" stopOpacity={0.2} /> {/* Dark green - Excellent (13-4) */}
                        <stop offset="100%" stopColor="#166534" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    
                    {/* Colored background sections with correct thresholds */}
                    <ReferenceArea y1={21} y2={28} fill="#DC2626" fillOpacity={0.15} /> {/* Poor: 21+ */}
                    <ReferenceArea y1={17} y2={21} fill="#EA580C" fillOpacity={0.15} /> {/* Fair: 17-20 */}
                    <ReferenceArea y1={13} y2={17} fill="#2563EB" fillOpacity={0.15} /> {/* Good: 13-16 */}
                    <ReferenceArea y1={4} y2={13} fill="#166534" fillOpacity={0.15} /> {/* Excellent: <12 (4-12) */}
                    
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    {/* XAxis required for syncId - hidden but present */}
                    <XAxis 
                      dataKey="date" 
                      hide
                    />
                    {/* Left Y-axis for Hooper Index (4-28) with even number ticks */}
                    <YAxis 
                      orientation="left"
                      stroke="#2563EB"
                      fontSize={12}
                      domain={[4, 28]}
                      ticks={[4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 26, 28]}
                      allowDecimals={false}
                      label={{ 
                        value: 'Hooper', 
                        angle: -90, 
                        position: 'insideLeft', 
                        style: { fontSize: 12, fill: '#2563EB' } 
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/* Hooper Index line - plotted directly, no shift */}
                    <Line 
                      type="monotone" 
                      dataKey="hooperIndex" 
                      stroke="#2563EB" 
                      strokeWidth={3}
                      dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#2563EB', strokeWidth: 2 }}
                      name="Hooper Index"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Chart B (Bottom): Wellness Metrics Only */}
            <div className="bg-background/50 rounded-lg p-4 border border-border/50">
              <h4 className="text-sm font-medium text-text-secondary mb-2">Wellness Metrics</h4>
              <div style={{ height: '240px', width: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={filteredData} 
                    syncId="wellnessSync"
                    margin={{ top: 5, right: 60, left: 60, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    {/* XAxis shown in bottom chart with same dataKey */}
                    <XAxis 
                      dataKey="date" 
                      stroke="#64748B"
                      fontSize={12}
                      tickFormatter={(value) => new Date(value)?.toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short'
                      })}
                    />
                    {/* Left Y-axis for wellness metrics (1-7) */}
                    <YAxis 
                      stroke="#64748B"
                      fontSize={12}
                      domain={[1, 7]}
                      ticks={[1, 2, 3, 4, 5, 6, 7]}
                      allowDecimals={false}
                      label={{ 
                        value: 'Wellness Score', 
                        angle: -90, 
                        position: 'insideLeft', 
                        style: { fontSize: 12, fill: '#64748B' } 
                      }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    {/* Individual wellness metrics - conditionally rendered based on visibility */}
                    {visibleMetrics?.sleep && (
                      <Line 
                        type="monotone" 
                        dataKey="sleep" 
                        stroke="#059669" 
                        strokeWidth={2}
                        dot={{ fill: '#059669', strokeWidth: 1, r: 3 }}
                        activeDot={{ r: 5, stroke: '#059669', strokeWidth: 2 }}
                        name="Sleep"
                      />
                    )}
                    {visibleMetrics?.fatigue && (
                      <Line 
                        type="monotone" 
                        dataKey="fatigue" 
                        stroke="#D97706" 
                        strokeWidth={2}
                        dot={{ fill: '#D97706', strokeWidth: 1, r: 3 }}
                        activeDot={{ r: 5, stroke: '#D97706', strokeWidth: 2 }}
                        name="Fatigue"
                      />
                    )}
                    {visibleMetrics?.soreness && (
                      <Line 
                        type="monotone" 
                        dataKey="soreness" 
                        stroke="#DC2626" 
                        strokeWidth={2}
                        dot={{ fill: '#DC2626', strokeWidth: 1, r: 3 }}
                        activeDot={{ r: 5, stroke: '#DC2626', strokeWidth: 2 }}
                        name="Soreness"
                      />
                    )}
                    {visibleMetrics?.stress && (
                      <Line 
                        type="monotone" 
                        dataKey="stress" 
                        stroke="#7C3AED" 
                        strokeWidth={2}
                        dot={{ fill: '#7C3AED', strokeWidth: 1, r: 3 }}
                        activeDot={{ r: 5, stroke: '#7C3AED', strokeWidth: 2 }}
                        name="Stress"
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              
              {/* Wellness metrics legend with checkboxes - moved below chart */}
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-3 border-t border-border text-sm">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={visibleMetrics?.sleep}
                    onChange={() => toggleMetric('sleep')}
                    className="w-4 h-4 rounded border-gray-300 text-[#059669] focus:ring-[#059669]"
                  />
                  <div className="w-3 h-3 rounded-full bg-[#059669]" />
                  <span className="text-text-secondary">Sleep</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={visibleMetrics?.fatigue}
                    onChange={() => toggleMetric('fatigue')}
                    className="w-4 h-4 rounded border-gray-300 text-[#D97706] focus:ring-[#D97706]"
                  />
                  <div className="w-3 h-3 rounded-full bg-[#D97706]" />
                  <span className="text-text-secondary">Fatigue</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={visibleMetrics?.soreness}
                    onChange={() => toggleMetric('soreness')}
                    className="w-4 h-4 rounded border-gray-300 text-[#DC2626] focus:ring-[#DC2626]"
                  />
                  <div className="w-3 h-3 rounded-full bg-[#DC2626]" />
                  <span className="text-text-secondary">Soreness</span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={visibleMetrics?.stress}
                    onChange={() => toggleMetric('stress')}
                    className="w-4 h-4 rounded border-gray-300 text-[#7C3AED] focus:ring-[#7C3AED]"
                  />
                  <div className="w-3 h-3 rounded-full bg-[#7C3AED]" />
                  <span className="text-text-secondary">Stress</span>
                </label>
              </div>
            </div>
          </div>
        </div>)
      )}
    </div>
  );
};

export default WellnessTrendChart;